import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useCreateRepository } from '../contexts/repositories';
import { CREATE_STEPS, EMPTY_VALUES, toCreatePayload, validate } from '../utils/form';
import FormSteps, { errorsForStep } from '../../../common/components/form-steps/form-steps';
import RepositoryFields from './fields';

import '../../clients/styles/form.css';

export default function CreateRepositoryForm() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const createRepository = useCreateRepository();

    const [values, setValues] = useState(() => ({
        ...EMPTY_VALUES,
        project_id: params.get('project') || '',
    }));
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(0);

    const isLast = step === CREATE_STEPS.length - 1;

    // Forward validates ONLY this step's fields: an error has to appear next
    // to the field that caused it, not three screens later. Backward validates
    // nothing — nothing has been sent yet.
    const next = () => {
        const found = errorsForStep(validate(values, { withProject: true }), CREATE_STEPS[step].fields);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }
        setStep(s => s + 1);
    };

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Enter on any earlier step advances instead of sending: a form with a
        // single visible button teaches the keyboard the wrong thing.
        if (!isLast) {
            next();
            return;
        }

        const found = validate(values, { withProject: true });
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            const created = await createRepository.mutateAsync(toCreatePayload(values));
            toast.success('Repositorio creado');
            // Straight to its screen, which is where the environments are added.
            navigate(`/admin/repositories/${created.id}`, { replace: true });
        } catch (err) {
            // A remote that cannot be cloned answers 422 and not 400, so its
            // message travels in `message` and not in `fields`.
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo crear el repositorio');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">Nuevo repositorio</h2>
                <p className="client-form__subtitle">
                    La base de código de un proyecto. Pertenece a uno solo y no se mueve
                    de ahí: las tareas se escriben contra él.
                </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <FormSteps steps={CREATE_STEPS} current={step} onStep={setStep} />

                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos del repositorio</h3>
                        <p className="client-form__section-text">
                            De qué proyecto es y desde dónde se clona.
                        </p>
                    </div>

                    <RepositoryFields
                        visible={CREATE_STEPS[step].fields}
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={createRepository.isPending}
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/projects?tab=repositorios" className="client-form__cancel">Cancelar</Link>
                    {step > 0 && (
                        <button
                            type="button"
                            className="client-form__back"
                            onClick={() => setStep(s => s - 1)}
                            disabled={createRepository.isPending}
                        >
                            Atrás
                        </button>
                    )}
                    {!isLast
                        ? (
                            <button type="button" className="client-form__submit" onClick={next}>
                                Siguiente
                            </button>
                        )
                        : (
                            <button
                                type="submit"
                                className="client-form__submit"
                                disabled={createRepository.isPending}
                            >
                                {createRepository.isPending ? 'Guardando...' : 'Crear repositorio'}
                            </button>
                        )}
                </div>
            </form>
        </section>
    );
}
